import { Alert, FeatureErrorsType, ErrorsType } from '../types'

import { Conciseness, Grammar, Other, Plagiarism, Punctuation, Spelling, WordChoice, Readability } from './features'
import { PlagiarismHelper } from './plagiarism-helper'

/**
 * Helper function to parse alerts from the response and map their count into the group/category
 * shown in the Report Summary section.
 * @param alerts Alerts from api response
 * @returns An array of FeatureErrorsType, where each error of a particular feature is mapped to associated category.
 */
export const parseAlerts = (alerts: Alert[] = []): FeatureErrorsType[] => {
  const featuresToShow: FeatureErrorsType[] = [
    {
      feature: Grammar,
      group: 'Grammar',
      total: 0,
      categories: [],
    },
    {
      feature: Spelling,
      group: 'Spelling',
      total: 0,
      categories: [],
    },
    {
      feature: Punctuation,
      group: 'Punctuation',
      total: 0,
      categories: [],
    },
    {
      feature: Conciseness,
      group: 'Style',
      mapToCategory: 'Wordiness',
      total: 0,
      categories: [],
    },
    {
      feature: Readability,
      group: 'Readability',
      mapToCategory: 'Readability',
      total: 0,
      categories: [],
    },
    {
      feature: WordChoice,
      group: 'Enhancement',
      mapToCategory: 'WordChoice',
      total: 0,
      categories: [],
    },
  ]
  const plagiarism = { ...defaultPlagiarism }
  const other = { ...defaultOther }
  alerts.forEach((alert) => {
    if (alert.group === 'Plagiarism') {
      plagiarism.total += alert.count
      plagiarism.feature.title =
        plagiarism.total >= 10
          ? 'Significant plagiarism'
          : plagiarism.total >= PlagiarismHelper.MIN_THRESHOLD
            ? 'Plagiarism found'
            : 'No plagiarism'
    } else {
      const feature = featuresToShow[featuresMap[alert.group] - 1]
      // some alerts should be mapped by category instead of group
      if (feature && feature.mapToCategory?.toUpperCase() === alert.category?.toUpperCase()) {
        feature.total += alert.count
        feature.categories.push({ category: alert.category, total: alert.count })
      } else if (feature && !feature.mapToCategory) {
        feature.total += alert.count
        feature.categories.push({ category: alert.category, total: alert.count })
      } else {
        other.total += alert.count
        other.categories[0].total += alert.count
      }
    }
  })

  return [plagiarism, ...featuresToShow, other]
}

export const getTotalErrors = (errors: Array<ErrorsType> = []): number => {
  return errors.reduce((prev, { group, total = 0 }) => {
    prev += PlagiarismHelper.isTextPlagiarism(group) ? 0 : total
    return prev
  }, 0)
}

const defaultPlagiarism: FeatureErrorsType = {
  feature: { ...Plagiarism, title: 'No plagiarism found' },
  group: 'Plagiarism',
  total: 0,
  categories: [],
}

const defaultOther: FeatureErrorsType = {
  feature: Other,
  group: 'Other',
  total: 0,
  mapToCategory: 'Other',
  categories: [
    {
      category: Other.title,
      total: 0,
    },
  ],
}

const featuresMap: { [a: string]: number } = {
  Grammar: 1,
  Spelling: 2,
  Punctuation: 3,
  Style: 4,
  Readability: 5,
  Enhancement: 6,
  'Suggested phrases': 7,
}
